// Modo equipe ("caixa de supermercado") — 213 v2 · 02/10/2026.
// Só liga quando a conta logada é uma conta de equipe (app_metadata.equipe === true, posta pelo servidor).
// Para todas as outras pessoas, NADA muda: os componentes abaixo só devolvem o que receberam.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { carregarMinhasTelas, ehContaDeEquipe, rotaPermitidaNoModoEquipe, type MinhasTelas, type TelaEquipe } from '@/lib/equipeLink';

interface EstadoModoEquipe {
  verificado: boolean; // já olhou a sessão deste celular (antes disso, nada muda)
  ativo: boolean;
  carregando: boolean;
  dados: MinhasTelas | null;
  recarregar: () => Promise<void>; // olha a sessão de novo AGORA (use depois de entrar pelo link)
}

const ContextoModoEquipe = createContext<EstadoModoEquipe>({
  verificado: false,
  ativo: false,
  carregando: false,
  dados: null,
  recarregar: async () => {},
});

export function useModoEquipe() {
  return useContext(ContextoModoEquipe);
}

export function ProvedorModoEquipe({ children }: { children: ReactNode }) {
  const [verificado, setVerificado] = useState(false);
  const [ativo, setAtivo] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [dados, setDados] = useState<MinhasTelas | null>(null);
  const vivo = useRef(true);

  const avaliar = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const equipe = ehContaDeEquipe(data.session?.user ?? null);
    if (!vivo.current) return;
    if (!equipe) {
      setAtivo(false);
      setDados(null);
      setCarregando(false);
      setVerificado(true);
      return;
    }
    setCarregando(true);
    const d = await carregarMinhasTelas();
    if (!vivo.current) return;
    setDados(d);
    setAtivo(true);
    setCarregando(false);
    setVerificado(true);
  }, []);

  useEffect(() => {
    vivo.current = true;
    void avaliar();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      // fora do callback (o supabase pede para não chamar o banco dentro dele)
      setTimeout(() => void avaliar(), 0);
    });
    return () => {
      vivo.current = false;
      sub.subscription.unsubscribe();
    };
  }, [avaliar]);

  return (
    <ContextoModoEquipe.Provider value={{ verificado, ativo, carregando, dados, recarregar: avaliar }}>
      {children}
    </ContextoModoEquipe.Provider>
  );
}

// Envolve as rotas: no modo equipe, qualquer rota que não seja da função volta para /servir.
export function TravaModoEquipe({ children }: { children: ReactNode }) {
  const { ativo, dados } = useModoEquipe();
  const { pathname } = useLocation();
  if (!ativo) return <>{children}</>;
  if (rotaPermitidaNoModoEquipe(pathname, dados)) return (
    <>
      <FaixaQuemSou />
      {children}
    </>
  );
  return <Navigate to="/servir" replace />;
}

// A tela da função que está aberta agora (para a barraca, a do ?ponto= certo).
export function telaAtual(pathname: string, search: string, dados: MinhasTelas | null): TelaEquipe | null {
  const telas = dados?.telas ?? [];
  const exata = telas.find((t) => t.url === pathname + search);
  if (exata) return exata;
  return telas.find((t) => {
    const base = t.url.split('?')[0];
    return pathname === base || pathname.startsWith(base + '/');
  }) ?? null;
}

// Faixa fininha no topo das telas da função: quem é e qual a função DESTE celular
// (ex.: "Pedro · Caixa 02 · Point · 4683", "Maria · Barraca Bebidas", "João · Portaria").
export function FaixaQuemSou() {
  const { ativo, dados } = useModoEquipe();
  const { pathname, search } = useLocation();
  if (!ativo || !dados || pathname === '/servir' || pathname.startsWith('/equipe/')) return null;
  const atual = telaAtual(pathname, search, dados);
  if (!atual) return null;
  const nome = (dados.nome ?? '').trim();
  return (
    <div className="flex h-7 w-full items-center justify-center gap-1.5 bg-foreground-950 px-3 text-xs font-bold text-white">
      <i className="ri-user-smile-line" />
      <span className="truncate">{nome ? `${nome} · ` : ''}{atual.rotulo}</span>
    </div>
  );
}

// Esconde no modo equipe (topo, menu, rodapé, faixas e avisos).
export function ForaDoModoEquipe({ children }: { children: ReactNode }) {
  const { ativo } = useModoEquipe();
  return ativo ? null : <>{children}</>;
}